import React, { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Mail, Lock, ArrowLeftCircle, Rocket, MailQuestion, AlertCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";
import api from "../services/api";
import ReactGA from 'react-ga4';

export default function LoginPage() {
  const { login, loginWithToken } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await login(email, password);
      ReactGA.event({
    category: "button",
    action: "click",
    label: "Login.Login Clicked",
  });
      navigate("/reports/Dep/Report");
    } catch (err) {
      setError(t("loginError"));
      setShowModal(true); // ✨ trigger styled modal
    }
  };

  const handleDemoAccess = async () => {
    try {
      const res = await api.post("/auth/demo-login");
      loginWithToken(res.data);
      navigate("/");
        ReactGA.event({
    category: "button",
    action: "click",
    label: "Login.Demo Env Clicked",
  });
    } catch (err) {
      console.error("Demo login failed:", err.response?.data || err.message);
      alert("Demo login failed");
    }
  };
  

      const whatsappMessage = encodeURIComponent(t("whatsappAutoMessage"));
      const whatsappLink = `https://wa.me/966582076578?text=${whatsappMessage}`;
  return (

    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-gray-100 px-4 relative overflow-hidden">
      <LanguageSwitcher />
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="relative bg-white p-8 rounded-2xl shadow-2xl w-full max-w-sm border-t-4 border-blue-500 z-10"
      >
        <Link
          to="/"
          className="absolute top-4 left-4 text-blue-500 hover:text-blue-700 transition"
          title="Back to Home"
        >
          <ArrowLeftCircle size={28} />
        </Link>

        <h2 className="text-3xl font-extrabold text-center text-gray-800 mb-6">
          {t("loginTitle")}
        </h2>

        {error && (
          <div className="bg-red-100 text-red-600 p-2 rounded-md mb-4 text-center text-sm font-medium">
            {error}
          </div>
        )}

        <form onSubmit={handleLogin}>
          <div className="relative mb-4">
            <Mail className="absolute top-3 left-3 text-gray-400" size={20} />
            <input
              type="text"
              placeholder={t("loginEmailPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border rounded-md focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
            />
          </div>

          <div className="relative mb-4">
            <Lock className="absolute top-3 left-3 text-gray-400" size={20} />
            <input
              type="password"
              placeholder={t("loginPasswordPlaceholder")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full pl-10 pr-3 py-2 border rounded-md focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none transition"
            />
          </div>

          <motion.button
            type="submit"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 text-white font-semibold py-2 rounded-md shadow-md hover:shadow-xl transition duration-200"
          >
            {t("loginButton")}
          </motion.button>
        </form>

        <div className="mt-8 space-y-3 text-sm text-center text-gray-700">
          <p>{t("loginNoCredentials")}</p>

          <button
            onClick={handleDemoAccess}
            className="w-full flex justify-center items-center gap-2 bg-white text-blue-600 border border-blue-500 rounded-md py-2 font-semibold hover:bg-blue-50 transition"
          >
            <Rocket size={16} /> {t("tryDemoLogin")}
          </button>

          <Link
            to="/ContactUsPage"
            className="inline-flex items-center justify-center gap-1 text-blue-600 hover:underline"
          >
            <MailQuestion size={16} /> {t("contactSupport")}
          </Link>
        </div>
      </motion.div>

      {/* 🌟 Modal */}
<AnimatePresence>
  {showModal && (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center px-4"
    >
      <motion.div
        initial={{ y: "-20%", opacity: 0, scale: 0.95 }}
        animate={{ y: 0, opacity: 1, scale: 1 }}
        exit={{ y: "-20%", opacity: 0, scale: 0.95 }}
        transition={{ duration: 0.35, ease: "easeInOut" }}
        className="bg-white p-8 rounded-2xl shadow-2xl w-full max-w-md border-t-4 border-red-500"
      >
        <div className="flex flex-col items-center space-y-4">
          <div className="bg-red-100 text-red-600 rounded-full p-3">
            <AlertCircle size={32} />
          </div>

          <h3 className="text-xl font-bold text-gray-800">
            {t("invalidPopupTitle")}
          </h3>

          <p className="text-gray-600 text-sm px-2">
            {t("invalidPopupMessage")}
          </p>

          <div className="mt-4 w-full flex flex-col md:flex-row gap-3">
            <button
              onClick={handleDemoAccess}
              className="w-full bg-gradient-to-r from-cyan-600 to-blue-600 text-white py-2 rounded-md font-semibold shadow hover:shadow-lg hover:brightness-110 transition"
            >
              {t("invalidPopupTryDemo")}
            </button>
            <button
              onClick={() => setShowModal(false)}
              className="w-full border border-gray-300 text-gray-700 py-2 rounded-md hover:bg-gray-50 transition"
            >
              {t("invalidPopupRetry")}
            </button>
          </div>

          {/* ✅ WhatsApp CTA */}
            <div className="mt-6 text-sm text-gray-600 text-center">
              <span className="block mb-2">
                💡 {t("whatsappHelpMessage")}
              </span>
              <a
                href= {whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                title="Chat with Us"
                className="inline-flex items-center justify-center px-4 py-2 text-sm font-semibold rounded-md bg-green-500 text-white hover:bg-green-600 transition"
              >
              💬 {t("chatNow")}
            </a>
          </div>
        </div>
      </motion.div>
    </motion.div>
  )}
</AnimatePresence>

    </div>
  );
}
